"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import SectionHeader from "@/components/common/SectionHeader";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface WeaveItem {
  id: string;
  title: Record<Lang, string>;
  badge: string;
  to: string;
  structure: Record<Lang, string>;
  application: Record<Lang, string>;
  meshRange: string;
}
const WEAVE_DATA: WeaveItem[] = [{
  id: "plain-weave",
  title: {
    en: "Plain Weave",
    zh: "平纹编织",
    es: "Tejido liso",
    de: "Glatte Bindung",
    fr: "Tissage uni",
    pt: "Ponto liso"
  },
  badge: "25µm – 10,000µm",
  to: "/PlainWeaveWireMeshPage",
  structure: {
    en: "Each weft wire passes alternatively over and under each warp wire at 90-degree intersections.",
    zh: "每根经丝与每根纬丝呈90度交替交叉穿行，孔径规整一致。",
    es: "Cada alambre de trama pasa alternativamente por encima y por debajo de cada alambre de urdimbre en intersecciones a 90 grados.",
    de: "Jeder Schussdraht läuft abwechselnd über und unter jedem Kettdraht bei rechtwinkliger Kreuzung.",
    fr: "Chaque fil de trame passe alternativement sur et sous chaque fil de chaîne à angle droit.",
    pt: "Cada arame de trama passa alternadamente por cima e por baixo de cada arame de urdidura em interseções de 90 graus."
  },
  application: {
    en: "Standard industrial screening, solid separation, and general liquid straining.",
    zh: "标准工业筛分、颗粒分级以及通用液体过滤过滤筒与滤盘。",
    es: "Cribado industrial estándar, separación de sólidos y colado general de líquidos.",
    de: "Standard-Industriesiebung, Feststofftrennung und allgemeine Flüssigkeitsfiltration.",
    fr: "Tamisage industriel standard, séparation de solides et filtration générale de liquides.",
    pt: "Peneiramento industrial padrão, separação de sólidos e filtragem geral de líquidos."
  },
  meshRange: "2 – 500 Mesh"
}, {
  id: "twilled-weave",
  title: {
    en: "Twilled Weave",
    zh: "斜纹编织",
    es: "Tejido asargado",
    de: "Köperbindung",
    fr: "Tissage croisé",
    pt: "Ponto sarjado"
  },
  badge: "20µm – 1,000µm",
  to: "/TwilledWeaveWireMeshPage",
  structure: {
    en: "Each weft wire passes over two warp wires and then under two warp wires in continuous sequence.",
    zh: "经纬丝双股跨接交叉编织，成型致密，能在同等孔径下使用更粗线径。",
    es: "Cada alambre de trama pasa sobre dos alambres de urdimbre y luego por debajo de dos en secuencia continua.",
    de: "Jeder Schussdraht verläuft fortlaufend über zwei und unter zwei Kettdrähten.",
    fr: "Chaque fil de trame passe sur deux fils de chaîne puis sous deux fils en séquence continue.",
    pt: "Cada arame de trama passa sobre dois arames de urdidura e sob dois arames em sequência contínua."
  },
  application: {
    en: "High-pressure fluid straining, abrasive slurries, and heavier mechanical load duties.",
    zh: "高压流体过滤、磨损性泥浆截留及要求高承载的机械过滤工况。",
    es: "Filtración de fluidos a alta presión, lodos abrasivos y cargas mecánicas elevadas.",
    de: "Hochdruck-Flüssigkeitsfiltration, abrasive Schlämme und hohe mechanische Belastungen.",
    fr: "Filtration de fluides sous haute pression, boues abrasives et fortes charges mécaniques.",
    pt: "Filtragem de fluidos sob alta pressão, lamas abrasivas e condições de alta carga mecânica."
  },
  meshRange: "20 – 635 Mesh"
}, {
  id: "plain-dutch-weave",
  title: {
    en: "Plain Dutch Weave",
    zh: "平纹荷兰编织",
    es: "Tejido holandés liso",
    de: "Glatte Tresse",
    fr: "Tissu hollandais uni",
    pt: "Trama holandesa lisa"
  },
  badge: "10µm – 300µm",
  to: "/PlainDutchWeavePage",
  structure: {
    en: "Coarse warp wires woven with tightly beaten, thinner weft wires forming zero-light wedge passages.",
    zh: "粗经丝配合密排细纬丝编织，形成致密的微细楔形弯曲过滤通道。",
    es: "Alambres gruesos de urdimbre con tramas delgadas y densas formando pasajes cuneiformes continuos.",
    de: "Stärkere Kettdrähte dicht mit dünneren Schussdrähten gewebt für stabile keilförmige Strömungspfade.",
    fr: "Fils de chaîne épais associés à des fils de trame fins et serrés formant des canaux cunéiformes.",
    pt: "Arames grossos de urdidura com tramas finas e compactas formando passagens cuneiformes estáveis."
  },
  application: {
    en: "Hydraulic system filters, petrochemical fuel clarification, and pressure line filtration.",
    zh: "液压管路过滤器、石油化工燃油净化及高压管网微粒截留。",
    es: "Filtros de sistemas hidráulicos, clarificación de combustibles petroquímicos y líneas de presión.",
    de: "Hydraulikfilter, Klärung petrochemischer Brennstoffe und Druckleitungsfiltration.",
    fr: "Filtres hydrauliques, clarification de carburants pétrochimiques et filtration sous pression.",
    pt: "Filtros de sistemas hidráulicos, clarificação de combustíveis petroquímicos e linhas de pressão."
  },
  meshRange: "12×64 – 80×700 Mesh"
}, {
  id: "twilled-dutch-weave",
  title: {
    en: "Twilled Dutch Weave",
    zh: "斜纹荷兰编织",
    es: "Tejido holandés asargado",
    de: "Köpertresse",
    fr: "Tissu hollandais croisé",
    pt: "Trama holandesa sarjada"
  },
  badge: "1µm – 100µm",
  to: "/TwillDutchWeavePage",
  structure: {
    en: "Twilled weave combined with Dutch dense weft packing for ultra-fine sub-micron filtration barriers.",
    zh: "结合斜纹编排与高密度荷兰密纹工艺，实现微米级极细固液气分离阻隔。",
    es: "Tejido asargado combinado con densa disposición holandesa para filtración ultrafina.",
    de: "Köperbindung kombiniert mit extrem dichter Schussanordnung für ultrafeine Filtration.",
    fr: "Armure croisée combinée à un serrage hollandais dense pour une filtration micrométrique ultra-fine.",
    pt: "Trama sarjada combinada com disposição holandesa densa para filtração ultrafina."
  },
  application: {
    en: "Critical aerospace fuel filtering, high-purity chemicals, and pharmaceutical gas drying.",
    zh: "航空航天燃料净化、高纯化学试剂过滤及制药级工艺气体脱水除杂。",
    es: "Filtración crítica de combustible aeroespacial, químicos de alta pureza y secado de gases farmacéuticos.",
    de: "Kritische Luftfahrt-Kraftstofffiltration, hochreine Chemikalien und pharmazeutische Gastrocknung.",
    fr: "Filtration critique de carburant aéronautique, chimie fine et séchage de gaz pharmaceutiques.",
    pt: "Filtragem crítica de combustível aeroespacial, químicos de alta pureza e secagem de gases farmacêuticos."
  },
  meshRange: "20×250 – 400×2800 Mesh"
}, {
  id: "reverse-dutch-weave",
  title: {
    en: "Reverse Dutch Weave",
    zh: "反向荷兰编织",
    es: "Tejido holandés inverso",
    de: "Panzertresse (Reverse Dutch)",
    fr: "Tissu hollandais inversé",
    pt: "Trama holandesa reversa"
  },
  badge: "15µm – 250µm",
  to: "/ReverseDutchWeavePage",
  structure: {
    en: "Inverted structure with thick weft wires and dense warp wires, delivering exceptional tensile rigidity.",
    zh: "采用密集粗经丝配合横向纬丝的反转结构，具备极高纵向机械抗拉强度。",
    es: "Estructura invertida con gruesos alambres de trama y densa urdimbre para alta rigidez a la tracción.",
    de: "Umgekehrte Struktur mit dichten Kettdrähten für überragende Zugfestigkeit in Längsrichtung.",
    fr: "Structure inversée avec chaîne dense pour une rigidité à la traction exceptionnelle.",
    pt: "Estrutura invertida com arames densos na urdidura para alta rigidez à tração longitudinal."
  },
  application: {
    en: "Continuous polymer melt screen changers, plastic extrusion auto-belts, and heavy centrifuges.",
    zh: "连续式聚合物熔体过滤滤带、塑料挤出机自动换网器以及离心过滤机。",
    es: "Cambiadores de malla continuos para polímeros fundidos, cintas de extrusión y centrífugas pesadas.",
    de: "Kontinuierliche Polymerschmelzefilter, Extrusions-Siebbänder und Hochleistungszentrifugen.",
    fr: "Changeurs de filtres continus pour polymères fondus, bandes d'extrusion et centrifugeuses industrielles.",
    pt: "Trocadores contínuos de tela para polímeros fundidos, esteiras de extrusão e centrífugas industriais."
  },
  meshRange: "48×10 – 720×150 Mesh"
}, {
  id: "five-heddle-weave",
  title: {
    en: "Five-Heddle Weave",
    zh: "五综编织",
    es: "Tejido de cinco lizos",
    de: "Fünfschaftgewebe",
    fr: "Tissage cinq lisses",
    pt: "Trama de cinco liços"
  },
  badge: "60µm – 1,500µm",
  to: "/FiveHeddleWeavePage",
  structure: {
    en: "Unique satin-like single-side smooth finish generated by a 5-shaft harness arrangement.",
    zh: "通过五综综框织造，形成一面光滑平整、一面易于导流反洗的非对称结构。",
    es: "Acabado liso satinado por una cara mediante disposición de arnés de 5 lizos.",
    de: "Einseitig glatte satinartige Oberfläche durch 5-Schäfter-Anordnung für leichte Reinigung.",
    fr: "Finition lisse type satin sur une face grâce à une disposition à 5 lisses.",
    pt: "Acabamento liso acetinado em uma das faces obtido por montagem de 5 liços."
  },
  application: {
    en: "Pressure leaf filters, fluid bed dryers, catalyst recovery, and easy-clean cake discharge.",
    zh: "叶片压滤机、流化床干燥筛板、催化剂回收以及要求快速反洗脱饼系统。",
    es: "Filtros de hojas a presión, secadores de lecho fluido, recuperación de catalizadores y desprendimiento rápido.",
    de: "Druckblattfilter, Wirbelschichttrockner, Katalysatorrückgewinnung und leichte Kuchenentfernung.",
    fr: "Filtres à plateaux sous pression, sécheurs à lit fluidisé, récupération de catalyseurs et décolmatage aisé.",
    pt: "Filtros de folhas sob pressão, secadores de leito fluidizado, recuperação de catalisadores e fácil descarte da torta."
  },
  meshRange: "12×40 – 80×300 Mesh"
}];
interface ArchCopy {
  sectionEyebrow: string;
  eyebrowLeft: string;
  eyebrowRight: string;
  heading: string;
  lead: string;
  weavePatternLabel: string;
  primaryTargetLabel: string;
  viewSpecs: string;
  alloyNoteTitle: string;
  alloyNoteBody: string;
  guidesBtn: string;
}
const copyData: Record<Lang, ArchCopy> = {
  en: {
    sectionEyebrow: "Weave Architectures",
    eyebrowLeft: "Precision Metallurgy",
    eyebrowRight: "DIN EN ISO 9044",
    heading: "Six Core Weave Architectures",
    lead: "Compare structural geometry, retention ratings, and mechanical strength across our precision stainless steel wire cloth profiles.",
    weavePatternLabel: "Weave Pattern",
    primaryTargetLabel: "Primary Target",
    viewSpecs: "View specifications",
    alloyNoteTitle: "Custom Alloys & Calendering:",
    alloyNoteBody: "All architectures are available in AISI 304, 316L, 904L, Inconel 600, Hastelloy C-22, and Monel 400 with optional diffusion bonding or rolling.",
    guidesBtn: "Engineering guides"
  },
  zh: {
    sectionEyebrow: "编织结构",
    eyebrowLeft: "精密冶金制造",
    eyebrowRight: "DIN EN ISO 9044",
    heading: "六大核心编织结构",
    lead: "对比 MutualFilter 精密不锈钢滤网在孔隙结构、过滤微米精度以及机械承压特性方面的核心规格。",
    weavePatternLabel: "结构特征",
    primaryTargetLabel: "典型应用",
    viewSpecs: "查看规格详情",
    alloyNoteTitle: "特殊合金与表面处理:",
    alloyNoteBody: "所有编织类型均支持 AISI 304、316L、904L、Inconel 600、Hastelloy C-22 及 Monel 400，并可选配压光（Calendering）及真空烧结固化。",
    guidesBtn: "技术计算指南"
  },
  es: {
    sectionEyebrow: "Estructuras de tejido",
    eyebrowLeft: "Metalurgia de precisión",
    eyebrowRight: "DIN EN ISO 9044",
    heading: "Seis arquitecturas básicas de tejido",
    lead: "Compare la geometría estructural, los rangos de retención y la resistencia mecánica en nuestros perfiles de tela metálica de acero inoxidable.",
    weavePatternLabel: "Patrón de tejido",
    primaryTargetLabel: "Aplicación principal",
    viewSpecs: "Ver especificaciones",
    alloyNoteTitle: "Aleaciones especiales y calandrado:",
    alloyNoteBody: "Todas las arquitecturas están disponibles en AISI 304, 316L, 904L, Inconel 600, Hastelloy C-22 y Monel 400 con calandrado o sinterizado opcional.",
    guidesBtn: "Guías de ingeniería"
  },
  de: {
    sectionEyebrow: "Webstrukturen",
    eyebrowLeft: "Präzisionsmetallurgie",
    eyebrowRight: "DIN EN ISO 9044",
    heading: "Sechs grundlegende Webstrukturen",
    lead: "Vergleichen Sie Strukturgeometrie, Rückhalteraten und mechanische Festigkeit unserer Präzisions-Edelstahldrahtgewebe.",
    weavePatternLabel: "Webstruktur",
    primaryTargetLabel: "Hauptanwendung",
    viewSpecs: "Spezifikationen ansehen",
    alloyNoteTitle: "Sonderlegierungen & Kalandrieren:",
    alloyNoteBody: "Alle Webarten sind in AISI 304, 316L, 904L, Inconel 600, Hastelloy C-22 und Monel 400 mit optionalem Kalandrieren oder Diffusionsglühen lieferbar.",
    guidesBtn: "Technische Leitfäden"
  },
  fr: {
    sectionEyebrow: "Structures de tissage",
    eyebrowLeft: "Métallurgie de précision",
    eyebrowRight: "DIN EN ISO 9044",
    heading: "Six architectures de tissage fondamentales",
    lead: "Comparez la géométrie structurelle, les seuils de rétention et la résistance mécanique de nos toiles en acier inoxydable de précision.",
    weavePatternLabel: "Structure de tissage",
    primaryTargetLabel: "Application principale",
    viewSpecs: "Voir les spécifications",
    alloyNoteTitle: "Alliages spéciaux et calandrage :",
    alloyNoteBody: "Toutes les armures sont disponibles en AISI 304, 316L, 904L, Inconel 600, Hastelloy C-22 et Monel 400 avec calandrage ou frittage en option.",
    guidesBtn: "Guides techniques"
  },
  pt: {
    sectionEyebrow: "Estruturas de tecelagem",
    eyebrowLeft: "Metalurgia de precisão",
    eyebrowRight: "DIN EN ISO 9044",
    heading: "Seis arquiteturas fundamentais de tecelagem",
    lead: "Compare a geometria estrutural, as taxas de retenção e a resistência mecânica de nossos perfis de telas em aço inoxidável.",
    weavePatternLabel: "Padrão de tecelagem",
    primaryTargetLabel: "Aplicação principal",
    viewSpecs: "Ver especificações",
    alloyNoteTitle: "Ligas especiais e calandragem:",
    alloyNoteBody: "Todas as arquiteturas estão disponíveis em AISI 304, 316L, 904L, Inconel 600, Hastelloy C-22 e Monel 400 com opção de calandragem ou sinterização.",
    guidesBtn: "Guias de engenharia"
  }
};
export default function Architectures() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <section id="coreweavearchitectures" data-nav="light" className="bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Section Header */}
        <SectionHeader id="coreweavearchitectures-header" eyebrow={copy.sectionEyebrow} heading={copy.heading} lead={copy.lead} />

        {/* 6 Weave Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {WEAVE_DATA.map((item, i) => <Reveal key={item.id} delay={Math.min(i * 50, 300)} className="flex">
              <div className="bg-card text-card-foreground border border-border rounded-lg p-6 sm:p-7 flex flex-col justify-between w-full">
                <div>
                  {/* Top Badge & Metric Row */}
                  <div className="flex items-center justify-between gap-3 mb-5">
                    <Badge variant="outline" className="bg-muted text-foreground font-mono text-[13px] px-2.5 py-1 border border-border">
                      {item.badge}
                    </Badge>
                    <span className="text-[13px] font-mono text-muted-foreground tracking-tight">
                      {item.meshRange}
                    </span>
                  </div>

                  {/* Title */}
                  <div className="mb-4">
                    <h3 className="mf-h3 font-bold text-card-foreground font-default">
                      {pick(lang, item.title)}
                    </h3>
                  </div>

                  {/* Weave Structure Specs */}
                  <div className="space-y-3 pt-2 text-sm">
                    <div>
                      <span className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                        {copy.weavePatternLabel}
                      </span>
                      <p className="text-card-foreground/90 leading-relaxed">
                        {pick(lang, item.structure)}
                      </p>
                    </div>

                    <div className="pt-2 border-t border-border/50">
                      <span className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                        {copy.primaryTargetLabel}
                      </span>
                      <p className="text-muted-foreground text-[13px] sm:text-sm leading-relaxed">
                        {pick(lang, item.application)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer Action */}
                <div className="pt-6 mt-6 border-t border-border/60 flex items-center justify-between">
                  <Button asChild variant="link" className="p-0 h-auto text-primary font-semibold text-sm group hover:no-underline">
                    <Link to={item.to} className="inline-flex items-center gap-1.5">
                      <span>{copy.viewSpecs}</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                  <span className="text-[13px] uppercase tracking-widest text-muted-foreground font-mono">
                    304 / 316L
                  </span>
                </div>
              </div>
            </Reveal>)}
        </div>

        {/* Supplementary Engineering Note */}
        <div className="mt-12 rounded-lg border border-border bg-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="text-[13px] sm:text-sm text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground mr-1.5">
              {copy.alloyNoteTitle}
            </span>
            {copy.alloyNoteBody}
          </div>

          <Button asChild variant="outline" size="sm" className="shrink-0 text-[13px]">
            <Link to="/TechnicalResourcesPage">
              {copy.guidesBtn}
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}